import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, take } from 'rxjs/operators';
import { ENVIRONMENT, NotificationService, WINDOW } from 'wflow-cdk';
import { CacheEventType } from './core/services/service-workers/cache.interface';
import { CACHE_SERVICE } from './core/services/service-workers/cache.service';
import { VersionService } from './core/services/version.service';

@Component({
  selector: 'wf-root',
  template: `
    <page-container
      [hideNavigation]="hideNavigation()"
      [showWorkspaceImmediately]="showWorkspaceImmediately()"
      [hideWorkspaceImmediately]="hideWorkspaceImmediately()"
    >
      <router-outlet />
    </page-container>
    <intercom-button />

    <wf-notifications-pile />
    <div data-plugin-slot="root"></div>
  `,
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  readonly notifierSvc = inject(NotificationService);
  readonly translateSvc = inject(TranslateService);
  readonly document = inject(DOCUMENT);
  readonly window = inject(WINDOW);
  readonly router = inject(Router);
  readonly destroyRef = inject(DestroyRef);
  readonly cacheSvc = inject(CACHE_SERVICE);
  readonly versionSvc = inject(VersionService);
  readonly cd = inject(ChangeDetectorRef);
  readonly env = inject(ENVIRONMENT);

  readonly routeUrl = toSignal(
    this.router.events.pipe(
      filter((v) => v instanceof NavigationEnd),
      map((v: NavigationEnd) => v.url),
    ),
  );

  readonly hideNavigation = computed(() => {
    return /registration|logout|unauthorize/.test(this.routeUrl());
  });

  readonly showWorkspaceImmediately = computed(() => {
    return /org/.test(this.routeUrl());
  });

  readonly hideWorkspaceImmediately = computed(() => {
    if (this.hideNavigation()) return true;

    return /maintenance|documents\/create/.test(this.routeUrl());
  });

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError,
        ),
        filter((event) => {
          const msg = event.error?.message ?? String(event.error);
          return /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Failed to load module script/i.test(
            msg,
          );
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const errorMsg = event.error?.message ?? String(event.error);
        this.handleChunkLoadError(event.url, errorMsg);
      });

    this.cacheSvc
      .onEvent(CacheEventType.VERSION_CHANGED)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => {
          const version = event?.data?.newVersion;
          return !!version && version !== this.versionSvc.currentVersion;
        }),
        take(1),
      )
      .subscribe(() => {
        this.showUpdateNotification();
      });
  }

  ngOnInit(): void {
    const link = this.document.createElement('LINK');
    link.setAttribute('rel', 'preconnect');
    link.setAttribute('href', `${this.env.mainApi}/api`);
    this.document.head.appendChild(link);

    // Use for internal product plugin
    // if ([[PLUGIN FEATURE FLAG]]) {
    //   this.pluginHostSvc.bootstrap();
    // }
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  private showUpdateNotification(): void {
    const toasterRef = this.notifierSvc.info(
      {
        text: this.translateSvc.instant('ServiceWorkerNewVersion'),
        title: this.translateSvc.instant('ServiceWorkerNewVersionTitle'),
      },
      {
        disableTimeOut: true,
        closeButton: true,
        progressBar: false,
      },
    );

    toasterRef.onTap
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadPage());
  }

  private handleChunkLoadError(url: string, errorMsg: string): void {
    fetch('_deploy/manifest.json', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((manifest) => {
        if (!manifest) {
          this.window.location.assign(url);
          return;
        }

        const allFiles = [
          ...(manifest.files ?? []),
          ...(manifest.retained ?? []).map((e: { file: string }) => e.file),
        ];

        const failedChunk = this.extractChunkFilename(errorMsg);
        if (
          failedChunk &&
          allFiles.some((f: string) => f.includes(failedChunk))
        ) {
          // The specific failing chunk is present in the manifest — retry navigation
          this.router.navigateByUrl(url);
        } else {
          this.window.location.assign(url);
        }
      })
      .catch(() => {
        this.window.location.assign(url);
      });
  }

  private extractChunkFilename(errorMsg: string): string | null {
    // Match patterns like "Loading chunk abc-123.js" or URLs ending with a JS filename
    const match = errorMsg.match(/[\w.-]+\.js/);
    return match ? match[0] : null;
  }

  private reloadPage(): void {
    this.cacheSvc
      .deleteAllStaticCache()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.document.location.reload();
      });
  }
}
